import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { startAutoTranslate, stopAutoTranslate } from "./autoTranslate";

/** Panel languages. The product is sold in Spain and the UK, so both are first-class:
 *  neither is a translation of the other and neither is the fallback for the customer. */
export const LANGUAGES: { code: Lang; label: string; report: string }[] = [
  { code: "es", label: "Español", report: "Spanish" },
  { code: "en", label: "English", report: "English" },
];
export type Lang = "es" | "en";

/** Keys are the English string, so an untranslated key still renders sensibly. */
const ES: Record<string, string> = {
  // navigation
  "Dashboard": "Panel",
  "Live Map": "Mapa en vivo",
  "Reconciliation": "Conciliación",
  "Journeys": "Trayectos",
  "Passengers": "Pasajeros",
  "CCTV & Evidence": "CCTV y evidencias",
  "Uber Import": "Importar viajes",
  "Vehicles": "Vehículos",
  "Drivers & Shifts": "Conductores y turnos",
  "Driver Scorecard": "Ranking de conductores",
  "Device Health": "Estado de equipos",
  "Reports": "Informes",
  "AI Assistant": "Asistente IA",
  "Settings": "Ajustes",
  "Occupancy": "Ocupación",

  // shell
  "TENANT": "CUENTA",
  "Sign out": "Cerrar sesión",
  "LIVE": "EN VIVO",
  "Import Uber CSV": "Importar CSV",
  "Add Vehicle": "Añadir vehículo",

  // AI quota / ride platforms
  "queries left today": "consultas disponibles hoy",
  "platform default": "límite por defecto",
  "Queries included in this fleet's daily allowance. Resets at 00:00 UTC.":
    "Consultas incluidas en el límite diario de esta flota. Se reinicia a las 00:00 UTC.",
  "This fleet has used all of today's included AI queries.":
    "Esta flota ha agotado las consultas de IA incluidas para hoy.",
  "The allowance resets at 00:00 UTC — everything else in the panel keeps working.":
    "El límite se reinicia a las 00:00 UTC; el resto del panel sigue funcionando con normalidad.",
  "Daily AI allowance used up — resets at 00:00 UTC":
    "Límite diario de IA agotado; se reinicia a las 00:00 UTC",

  // ride-platform connectors
  "Ride platforms · Uber & Bolt": "Plataformas · Uber y Bolt",
  "connected": "conectadas",
  "Connect a platform to pull trips and driver payments straight from its API instead of uploading a CSV every week. Credentials are stored per fleet and are never shown again once saved.":
    "Conecta una plataforma para traer viajes y pagos de conductor directamente desde su API, en vez de subir un CSV cada semana. Las credenciales se guardan por flota y no se vuelven a mostrar.",
  "ENABLED": "ACTIVA",
  "not connected": "sin conectar",
  "open portal": "abrir portal",
  "Client ID": "Client ID",
  "Client secret": "Client secret",
  "(blank keeps the stored one)": "(en blanco conserva la guardada)",
  "paste the secret": "pega el secreto",
  "Organisation ID": "ID de organización",
  "Sync window (hours)": "Ventana de sincronización (horas)",
  "Reads": "Lee",
  "freshness": "actualización",
  "Test connection": "Probar conexión",
  "Testing…": "Probando…",
  "Sync payments now": "Sincronizar pagos ahora",
  "Syncing…": "Sincronizando…",
  "Connect with": "Conectar con",
  "Credentials accepted.": "Credenciales aceptadas.",
  "Redirect URI to register in the platform console": "URI de redirección a registrar en la consola de la plataforma",
  "Privacy policy URL": "URL de la política de privacidad",
  "Approve access in the tab that just opened, then press Test":
    "Aprueba el acceso en la pestaña que se acaba de abrir y pulsa Probar conexión",

  // AI assistant screen
  "model": "modelo",
  "MCP tools": "herramientas MCP",
  "no LLM in the critical path": "ningún LLM en la ruta crítica",
  "Read aloud": "Lectura en voz alta",
  "Map": "Mapa",
  "on": "activada",
  "off": "desactivada",
  "Checking…": "Comprobando…",
  "API key configured": "Clave API configurada",
  "API key missing": "Falta la clave API",
  "Configure the API key in Settings first": "Configura antes la clave API en Ajustes",
  "Ask about your fleet…": "Pregunta sobre tu flota…",
  "New conversation": "Nueva conversación",
  "Ask about journeys, discrepancies, drivers, passengers or device health. Answers are grounded on tenant-scoped tools — never on guesses.":
    "Pregunta sobre trayectos, discrepancias, conductores, pasajeros o estado de los equipos. Las respuestas se basan en herramientas limitadas a tu flota, nunca en suposiciones.",
  "Which journeys in the last 24 h have no declared Uber trip?":
    "¿Qué trayectos de las últimas 24 h no tienen un viaje declarado en Uber?",
  "Summarise discrepancies per driver today":
    "Resume las discrepancias de hoy por conductor",
  "Which vehicles are offline right now?":
    "¿Qué vehículos están sin conexión ahora mismo?",
  "Show the lowest-scoring reconciliation records and why":
    "Muestra los registros de conciliación con menor puntuación y por qué",

  // common actions and states
  "Generate": "Generar",
  "Refresh": "Actualizar",
  "Save": "Guardar",
  "Cancel": "Cancelar",
  "Download": "Descargar",
  "Connect": "Conectar",
  "Connect all": "Conectar todas",
  "Stop all": "Parar todas",
  "Loading": "Cargando",
  "No data": "Sin datos",
  "Last 24 h": "Últimas 24 h",
  "Last 48 h": "Últimas 48 h",
  "Last 7 days": "Últimos 7 días",
  "Last 14 days": "Últimos 14 días",
  "Last 30 days": "Últimos 30 días",

  // dashboard / fleet vocabulary
  "Fleet overview": "Resumen de flota",
  "Vehicles online": "Vehículos conectados",
  "GPS journeys": "Trayectos GPS",
  "Uber trips declared": "Viajes declarados",
  "Match rate": "Tasa de coincidencia",
  "Requires review": "Requiere revisión",
  "Driver": "Conductor",
  "Drivers": "Conductores",
  "Plate": "Matrícula",
  "Trips": "Servicios",
  "Revenue": "Ingresos",
  "Fuel": "Combustible",
  "Net": "Neto",
  "Per km": "Por km",
  "Score": "Puntuación",
  "Harsh alerts": "Alertas de conducción",
  "Matched": "Coincide",
  "Fleet": "Flota",

  // dashboard
  "Physical journeys vs declared Uber trips \u00b7 last 24 h": "Trayectos f\u00edsicos frente a viajes declarados \u00b7 \u00faltimas 24 h",
  "Telemetry pulse < 5 min": "Se\u00f1al de telemetr\u00eda < 5 min",
  "Imported / declared": "Importados / declarados",
  "active": "activos",
  "matched": "coinciden",
  "possible": "posibles",
  "review": "en revisi\u00f3n",
  "unmatched": "sin emparejar",
  "Journeys per hour": "Trayectos por hora",
  "Latest reconciliation": "\u00daltima conciliaci\u00f3n",
  "View inbox": "Ver bandeja",

  // alerts
  "Live alerts \u00b7 harsh driving & collisions": "Alertas en vivo \u00b7 conducci\u00f3n brusca y colisiones",
  "sources: TrackSolid alarms \u00b7 camera analytics \u00b7 manual": "fuentes: alarmas TrackSolid \u00b7 anal\u00edtica de c\u00e1mara \u00b7 manual",
  "No active alerts. New alarms appear here instantly.": "Sin alertas activas. Las nuevas aparecen aqu\u00ed al instante.",
  "Confirm: this really was harsh driving": "Confirmar: fue conducci\u00f3n brusca de verdad",
  "False positive \u2014 do not count it against the driver": "Falso positivo \u2014 que no penalice al conductor",
  "Hide for now": "Ocultar por ahora",

  // scorecard
  "Driver scorecard": "Ranking de conductores",
  "Rank by: compliance": "Ordenar por: cumplimiento",
  "Rank by: revenue": "Ordenar por: ingresos",
  "Rank by: profit": "Ordenar por: beneficio",
  "Rank by: ROI per km": "Ordenar por: ROI por km",
  "Rank by: safety": "Ordenar por: seguridad",
  "Rank by: trips": "Ordenar por: servicios",
  "Rank by: balanced": "Ordenar por: equilibrado",
  "Commission": "Comisi\u00f3n",
  "Profit #": "N.\u00ba benef.",

  // vehicles / fuel
  "no fuel data": "sin datos de combustible",
  "Capabilities": "Capacidades",
  "Driver on shift": "Conductor en turno",
  "Device": "Equipo",
  "Vehicle": "Veh\u00edculo",

  // cctv
  "All \u00b7 Cabin": "Todas \u00b7 Cabina",
  "All \u00b7 Road": "Todas \u00b7 Carretera",
  "All \u00b7 Both": "Todas \u00b7 Ambas",
  "uses the vehicle's mobile data": "consume datos de la SIM del veh\u00edculo",
  "Capture now": "Capturar ahora",

  // tenants
  "Fleets": "Flotas",
  "New fleet": "Nueva flota",
  "Rename": "Renombrar",
  "Delete": "Eliminar",

  // reports
  "Executive report": "Informe ejecutivo",
  "Raw data exports": "Exportaciones de datos",
  "Daily": "Diario",
  "Weekly": "Semanal",
  "Monthly": "Mensual",
  "Annual": "Anual",
  "Profitability": "Rentabilidad",
  "Discrepancies": "Discrepancias",
};

const DICTS: Record<Lang, Record<string, string>> = { es: ES, en: {} };

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (s: string) => string; reportLanguage: string };
const I18nContext = createContext<Ctx>({ lang: "es", setLang: () => {}, t: (s) => s, reportLanguage: "Spanish" });

function detect(): Lang {
  try {
    const saved = localStorage.getItem("tg.lang") as Lang | null;
    if (saved === "es" || saved === "en") return saved;
  } catch { /* private mode */ }
  return (navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en";
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detect);
  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem("tg.lang", l); } catch { /* private mode */ }
    // Reload so the DOM translator applies/clears cleanly across every screen.
    window.location.reload();
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang === "es") startAutoTranslate(); else stopAutoTranslate();
  }, [lang]);
  const t = useCallback((s: string) => DICTS[lang][s] ?? s, [lang]);
  const value = useMemo(() => ({
    lang, setLang, t,
    reportLanguage: LANGUAGES.find((l) => l.code === lang)?.report || "English",
  }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }
export function useT() { return useContext(I18nContext).t; }

export function LanguageSwitch({ compact }: { compact?: boolean }) {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex rounded-md border border-[#26334d] overflow-hidden" data-testid="language-switch">
      {LANGUAGES.map((l) => (
        <button key={l.code} onClick={() => setLang(l.code)} data-testid={`language-${l.code}`}
                className={`font-mono text-[10px] px-2 py-1 ${lang === l.code ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200"}`}>
          {compact ? l.code.toUpperCase() : l.label}
        </button>
      ))}
    </div>
  );
}
